import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { showError, showInfo, showSuccess } from '@/lib/alert'
import { scrollToElement } from '@/lib/format'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useAuth } from '@/auth/AuthContext'
import { Preloader } from '@/components/Preloader'
import { UploadField } from '@/features/profileForm/UploadField'
import { StickyProgressBar } from '@/features/profileForm/StickyProgressBar'
import { PersonalInformationSection } from '@/features/profileForm/PersonalInformationSection'
import { EducationSection } from '@/features/profileForm/EducationSection'
import { WorkExperienceSection } from '@/features/profileForm/WorkExperienceSection'
import { OrganizationSection } from '@/features/profileForm/OrganizationSection'
import {
  AchievementSection,
  FamilySection,
  MedicalHistorySection,
} from '@/features/profileForm/SimpleSections'
import {
  optionsFrom,
  useDegrees,
  useIndustries,
  useMajors,
  useOrganizationTypes,
  useProvinces,
  useSavedAchievement,
  useSavedEducation,
  useSavedFamily,
  useSavedMedical,
  useSavedOrganization,
  useSavedPhoto,
  useSavedProfile,
  useSavedWorkExperience,
  useUniversities,
  useWorkLevels,
  photoDataUrl,
} from '@/features/profileForm/queries'
import {
  convertCv,
  educationFromCv,
  organizationsFromCv,
  personalInfoFromCv,
  workExperienceFromCv,
} from '@/features/profileForm/cvAutofill'
import {
  buildSaveAllForm,
  GENDER,
  JOB_PREFERENCES,
  MARITAL_STATUS,
  type AchievementState,
  type EducationRow,
  type FamilyState,
  type MedicalState,
  type OrganizationRow,
  type PersonalInfoState,
  type WorkExperienceRow,
} from '@/features/profileForm/types'
import './profileForm.css'

/**
 * Port of Views/Profile/Form.cshtml + wwwroot/js/app/profile/form.js.
 *
 * Structure follows the Razor exactly:
 *   col-md-8  photo/CV card, then seven section cards
 *   col-md-4  sticky "Profile Completeness" card, then the Submit button
 *
 * Four sections (Personal Information, Achievement, Family, Medical History) are
 * collapsible panels. The other three (Education, Work Experience, Organization)
 * are NOT collapsible: their header carries an "Add" button that opens a modal,
 * and the table below it is always visible. That asymmetry is the Razor's.
 *
 * All four now start OPEN. The Razor collapsed everything but Personal
 * Information, which hid three of the seven sections the completeness card asks
 * the candidate to finish — they had to be found and expanded before they could
 * even be seen. They still collapse on click.
 *
 * The Gemini CV autofill (`triggerAutofill`, `setCVData`, `populate*CV`) IS
 * ported — see `handleCvSelected` below and features/profileForm/cvAutofill.ts.
 * It differs from the original in one respect: the three list sections are only
 * replaced when the CV yielded rows AND the section is still empty, so
 * re-uploading cannot wipe rows the candidate typed by hand.
 *
 * On phones the `col-md-4` completeness card stacks below all seven sections,
 * so StickyProgressBar echoes it under the navbar until the real card scrolls
 * into view.
 */
const SECTION_LABELS = [
  'Personal Information',
  'Education',
  'Work Experience',
  'Organization Experience',
  'Achievement, Skill & Certification',
  'Relative / Family',
  'Medical History',
] as const

const SECTION_ICONS = [
  'fas fa-user',
  'fas fa-user-graduate',
  'fas fa-suitcase',
  'fas fa-address-card',
  'fas fa-certificate',
  'fas fa-users',
  'fas fa-notes-medical',
] as const

export function ProfileForm(): JSX.Element {
  usePageTitle('Profile')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const saved = useSavedProfile()
  const savedPhoto = useSavedPhoto()
  const savedEducation = useSavedEducation()
  const savedWork = useSavedWorkExperience()
  const savedOrg = useSavedOrganization()
  const savedAchievement = useSavedAchievement()
  const savedFamily = useSavedFamily()
  const savedMedical = useSavedMedical()

  const [personal, setPersonal] = useState<PersonalInfoState>({
    FullName: '',
    Email: '',
    PhoneNumber: '',
    PlaceOfBirth: '',
    DateOfBirth: '',
    Age: '',
    DomicileId: '',
    DomicileName: '',
    CityId: '',
    CountryCode: '',
    Address: '',
    JobPreferences: '',
    JobPreferencesLabel: '',
    ReligionCode: '',
    Gender: GENDER.Male,
    MaritalStatusId: MARITAL_STATUS.Single,
    LinkedIn: '',
    Instagram: '',
  })
  const [education, setEducation] = useState<EducationRow[]>([])
  const [work, setWork] = useState<WorkExperienceRow[]>([])
  const [organizations, setOrganizations] = useState<OrganizationRow[]>([])
  const [achievement, setAchievement] = useState<AchievementState>({
    IsHaveAchievement: false,
    AchievementDescription: '',
    SoftSkillDescription: '',
    TechnicalSkill: '',
  })
  // null = unanswered, so neither radio starts selected and one click completes
  // the section. See FamilyState in types.ts.
  const [family, setFamily] = useState<FamilyState>({
    IsHaveFamilyInTBG: null,
    FamilyInTBG: '',
    IsHaveFamilyInTelcoIndustry: null,
    FamilyInTelcoIndustry: '',
  })
  const [medical, setMedical] = useState<MedicalState>({
    IsHaveMedicalHistory: null,
    MedicalHistory: '',
  })

  const [photo, setPhoto] = useState<File | null>(null)
  const [cv, setCv] = useState<File | null>(null)
  const [certificate, setCertificate] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string>('')

  /** Watched by the mobile sticky meter so the two are never on screen together. */
  const progressCardRef = useRef<HTMLDivElement>(null)

  const [openModal, setOpenModal] = useState<'education' | 'work' | 'organization' | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isExtracting, setIsExtracting] = useState(false)

  // Lookups the CV extraction needs to turn model text into real ids.
  const provinces = useProvinces()
  const degrees = useDegrees()
  const universities = useUniversities()
  const majors = useMajors()
  const industries = useIndustries()
  const workLevels = useWorkLevels()
  const organizationTypes = useOrganizationTypes()

  /**
   * Port of `handleCVFile` -> `setCVData` -> `triggerAutofill`.
   *
   * The chosen PDF is sent to gemini/convertCV and whatever comes back is mapped
   * onto the form. Extraction failing is not an error the candidate has to act
   * on — the CV is still attached and every field can be typed by hand — so a
   * failure only reports that nothing could be read, exactly as the original
   * swallowed it and carried on.
   *
   * The three list sections are only replaced when the CV yielded rows AND the
   * section is still empty, so re-uploading never duplicates or silently wipes
   * rows the candidate entered by hand. The original overwrote unconditionally.
   */
  async function handleCvSelected(file: File | null): Promise<void> {
    setCv(file)
    if (!file) return

    setIsExtracting(true)
    try {
      const extract = await convertCv(file)
      if (!extract) {
        await showInfo('We could not read your CV automatically. Please fill the form manually.')
        return
      }

      const filled: string[] = []

      const patch = personalInfoFromCv(
        extract,
        optionsFrom(provinces.data?.Data, (p) => p.ProvinceID, (p) => p.ProvinceName),
      )
      if (Object.keys(patch).length > 0) {
        setPersonal((current) => ({ ...current, ...patch }))
        filled.push('Personal Information')
      }

      const educationRows = educationFromCv(
        extract,
        optionsFrom(degrees.data?.Data, (d) => d.mstDegreeID, (d) => d.DegreeName),
        optionsFrom(universities.data?.Data, (u) => u.mstUniversityID, (u) => u.UniversityName),
        optionsFrom(majors.data?.Data, (m) => m.mstMajorID, (m) => m.MajorName),
      )
      if (educationRows.length > 0 && education.length === 0) {
        setEducation(educationRows)
        filled.push(`Education (${educationRows.length})`)
      }

      const workRows = workExperienceFromCv(
        extract,
        optionsFrom(industries.data?.Data, (i) => i.mstIndustryID, (i) => i.IndustryName),
        optionsFrom(
          workLevels.data?.Data,
          (l) => l.mstWorkExperienceLevelID,
          (l) => l.WorkExperienceLevelName,
        ),
      )
      if (workRows.length > 0 && work.length === 0) {
        setWork(workRows)
        filled.push(`Work Experience (${workRows.length})`)
      }

      const orgRows = organizationsFromCv(
        extract,
        optionsFrom(
          organizationTypes.data?.Data,
          (t) => t.mstOrganizationTypeID,
          (t) => t.OrganizationType,
        ),
      )
      if (orgRows.length > 0 && organizations.length === 0) {
        setOrganizations(orgRows)
        filled.push(`Organization (${orgRows.length})`)
      }

      if (filled.length === 0) {
        await showInfo('We could not read your CV automatically. Please fill the form manually.')
        return
      }
      await showSuccess(
        `Filled from your CV: ${filled.join(', ')}. Please review every field before submitting.`,
      )
    } catch {
      await showInfo('We could not read your CV automatically. Please fill the form manually.')
    } finally {
      setIsExtracting(false)
    }
  }


  /**
   * Seeds the form from the signed-in account.
   *
   * `_personalinformation.js:405-406` filled the name from the saved candidate
   * record and fell back to the session only for the email. That leaves a
   * brand-new user staring at empty Full Name and Phone Number fields they
   * filled in on the registration form minutes earlier, because the candidate
   * record does not exist until the first save.
   *
   * Each field is only seeded while still blank, so this can never overwrite
   * something the user typed or something the saved record supplies.
   */
  useEffect(() => {
    if (!user) return
    setPersonal((p) => ({
      ...p,
      Email: p.Email || user.Email || '',
      FullName: p.FullName || user.FullName || '',
      PhoneNumber: p.PhoneNumber || user.PhoneNumber || '',
    }))
  }, [user])

  /** The inline script at the bottom of Form.cshtml previewed the chosen photo. */
  useEffect(() => {
    if (!photo) {
      setPhotoPreview('')
      return
    }
    const reader = new FileReader()
    reader.onload = (event) => setPhotoPreview(String(event.target?.result ?? ''))
    reader.readAsDataURL(photo)
  }, [photo])

  useEffect(() => {
    const data = saved.data?.Data
    if (!data) return
    setPersonal((p) => ({
      ...p,
      FullName: data.FullName ?? p.FullName,
      Email: data.Email ?? p.Email,
      // `?? p.PhoneNumber`, not `?? ''`: a saved record with no phone must not
      // wipe the number seeded from the account above.
      PhoneNumber: data.PhoneNumber ?? p.PhoneNumber,
      PlaceOfBirth: data.PlaceOfBirth ?? '',
      DateOfBirth: (data.DateOfBirth ?? '').slice(0, 10),
      Age: data.Age ? String(data.Age) : '',
      DomicileId: data.State_HCIS_ID ?? '',
      DomicileName: data.Domicile ?? '',
      CityId: data.City_HCIS_ID ?? '',
      CountryCode: data.Country_HCIS_Code ?? '',
      Address: data.Address ?? '',
      // Stored as the option's TEXT, because SaveAll sent
      // `getSelectedText("#slsJobPreferences")` rather than its value — so the
      // saved string has to be mapped back to a value to re-select the option.
      ...jobPreferenceFrom(data.JobPreferences),
      ReligionCode: data.ReligionCode ?? '',
      Gender: data.Gender ?? GENDER.Male,
      MaritalStatusId: data.mstMaritalStatusID ?? MARITAL_STATUS.Single,
      LinkedIn: data.LinkedIn ?? '',
      Instagram: data.Instagram ?? '',
    }))
  }, [saved.data])

  useEffect(() => {
    const rows = savedEducation.data?.Data
    if (!rows?.length) return
    setEducation(
      rows.map((row) => ({
        mstDegreeID: String(row.mstDegreeID ?? ''),
        DegreeName: row.DegreeName ?? '',
        mstUniversityID: String(row.mstUniversityID ?? ''),
        UniversityName: row.UniversityName ?? '',
        UniversityOther: row.UniversityOther ?? '',
        mstMajorID: String(row.mstMajorID ?? ''),
        MajorName: row.MajorName ?? '',
        MajorOther: row.MajorOther ?? '',
        GPA: row.GPA != null ? String(row.GPA) : '',
        MonthIn: String(row.MonthIn ?? ''),
        YearIn: String(row.YearIn ?? ''),
        MonthOut: String(row.MonthOut ?? ''),
        YearOut: String(row.YearOut ?? ''),
        isUntilNow: Boolean(row.isUntilNow),
      })),
    )
  }, [savedEducation.data])

  useEffect(() => {
    const rows = savedWork.data?.Data
    if (!rows?.length) return
    setWork(
      rows
        // The upstream stores one blank placeholder row when there is no work
        // history (see types.ts); it must not surface as an entry.
        .filter((row) => (row.Company ?? '').trim() !== '')
        .map((row) => ({
          Company: row.Company ?? '',
          mstIndustryID: String(row.mstIndustryID ?? ''),
          IndustryName: row.IndustryName ?? '',
          Position: row.Position ?? '',
          mstWorkExperienceLevelID: String(row.mstWorkExperienceLevelID ?? ''),
          LevelName: row.WorkExperienceLevelName ?? '',
          MonthIn: String(row.MonthIn ?? ''),
          YearIn: String(row.YearIn ?? ''),
          MonthOut: String(row.MonthOut ?? ''),
          YearOut: String(row.YearOut ?? ''),
          isUntilNow: Boolean(row.isUntilNow),
          Salary: row.Salary != null ? String(row.Salary) : '',
          JobDescription: row.JobDescription ?? '',
        })),
    )
  }, [savedWork.data])

  useEffect(() => {
    const rows = savedOrg.data?.Data
    if (!rows?.length) return
    setOrganizations(
      rows
        .filter((row) => (row.OrganizationName ?? '').trim() !== '')
        .map((row) => ({
          OrganizationName: row.OrganizationName ?? '',
          mstOrganizationTypeID: String(row.mstOrganizationTypeID ?? ''),
          OrganizationTypeName: row.OrganizationType ?? '',
          OrganizationPosition: String(row.OrganizationPosition ?? ''),
        })),
    )
  }, [savedOrg.data])

  useEffect(() => {
    const data = savedAchievement.data?.Data
    if (!data) return
    setAchievement({
      IsHaveAchievement: Boolean(data.IsHaveAchievement),
      AchievementDescription: data.AchievementDescription ?? '',
      SoftSkillDescription: data.SoftSkillDescription ?? '',
      TechnicalSkill: data.TechnicalSkill ?? '',
    })
  }, [savedAchievement.data])

  useEffect(() => {
    const data = savedFamily.data?.Data
    if (!data) return
    setFamily({
      IsHaveFamilyInTBG: Boolean(data.IsHaveFamilyInTBG),
      FamilyInTBG: data.FamilyInTBG ?? '',
      IsHaveFamilyInTelcoIndustry: Boolean(data.IsHaveFamilyInTelcoIndustry),
      FamilyInTelcoIndustry: data.FamilyInTelcoIndustry ?? '',
    })
  }, [savedFamily.data])

  useEffect(() => {
    const data = savedMedical.data?.Data
    if (!data) return
    setMedical({
      IsHaveMedicalHistory: Boolean(data.IsHaveMedicalHistory),
      MedicalHistory: data.MedicalHistory ?? '',
    })
  }, [savedMedical.data])

  const existingPhoto = saved.data?.Data?.PhotoPath ?? null
  const existingCv = saved.data?.Data?.CurriculumVitaePath ?? null
  /** The stored photo as a data: URL — see useSavedPhoto. */
  const savedPhotoUrl = photoDataUrl(savedPhoto.data?.Data)

  /**
   * Which of the seven sections count as done, for the completeness card.
   * progress-tracker.js drove this from DOM inspection; the same seven labels
   * are checked here against state.
   */
  const completed = useMemo<boolean[]>(
    () => [
      Boolean(
        personal.FullName.trim() &&
          personal.PhoneNumber.trim() &&
          personal.PlaceOfBirth.trim() &&
          personal.DateOfBirth &&
          personal.DomicileId &&
          personal.CityId &&
          personal.CountryCode &&
          personal.Address.trim() &&
          personal.JobPreferences &&
          personal.ReligionCode,
      ),
      education.length > 0,
      work.length > 0,
      organizations.length > 0,
      Boolean(achievement.SoftSkillDescription.trim() && achievement.TechnicalSkill.trim()),
      // Both questions must be answered, and a "Yes" needs its description.
      family.IsHaveFamilyInTBG !== null &&
        family.IsHaveFamilyInTelcoIndustry !== null &&
        (family.IsHaveFamilyInTBG === false || family.FamilyInTBG.trim().length > 0) &&
        (family.IsHaveFamilyInTelcoIndustry === false ||
          family.FamilyInTelcoIndustry.trim().length > 0),
      medical.IsHaveMedicalHistory !== null &&
        (medical.IsHaveMedicalHistory === false || medical.MedicalHistory.trim().length > 0),
    ],
    [personal, education, work, organizations, achievement, family, medical],
  )
  const completedCount = completed.filter(Boolean).length
  const percent = Math.round((completedCount / SECTION_LABELS.length) * 100)

  function validate(): string | null {
    // Relaxed in one way from the original, which checked `files.length` only
    // and so told a returning candidate to re-upload a CV they already had.
    if (!cv && !existingCv) return 'Please upload your CV before submitting.'
    if (!photo && !existingPhoto) return 'Please upload your photo before submitting.'

    const required: string[] = [
      personal.FullName,
      personal.Email,
      personal.PhoneNumber,
      personal.PlaceOfBirth,
      personal.DateOfBirth,
      personal.DomicileId,
      personal.CityId,
      personal.CountryCode,
      personal.Address,
      personal.JobPreferences,
      personal.ReligionCode,
    ]
    if (required.some((value) => !String(value).trim())) {
      return 'Please fill in all required fields correctly.'
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personal.Email)) {
      return 'Please fill in all required fields correctly.'
    }
    // SaveAll refused non-numeric ids: the upstream columns are ints.
    if (personal.DomicileId && !/^\d+$/.test(personal.DomicileId)) {
      return 'Domicile data is invalid. Please re-select your domicile from the dropdown.'
    }
    if (personal.CityId && !/^\d+$/.test(personal.CityId)) {
      return 'City data is invalid. Please re-select your city from the dropdown.'
    }
    if (education.length === 0) return 'Education has required fields that are not filled'
    if (!achievement.SoftSkillDescription.trim() || !achievement.TechnicalSkill.trim()) {
      return 'Achievement has required fields that are not filled'
    }
    return null
  }

  async function handleSubmit(): Promise<void> {
    setShowErrors(true)
    const problem = validate()
    if (problem) {
      await showError(problem)
      scrollToElement('.is-invalid')
      return
    }

    setIsSaving(true)
    try {
      const body = buildSaveAllForm({
        personal,
        education,
        work,
        organizations,
        achievement,
        family,
        medical,
        photo,
        cv,
        certificate,
      })
      await api.form('/api/ecareer/profile/saveAll', body)

      await queryClient.invalidateQueries({ queryKey: ['account', 'profile-completion'] })
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
      await showSuccess('All profile sections saved successfully')
      navigate('/Vacancy')
    } catch (error) {
      await showError(
        error instanceof Error ? error.message : 'An error occurred while saving your profile.',
      )
    } finally {
      setIsSaving(false)
    }
  }

  if (saved.isLoading) return <Preloader />

  return (
    <div className="content">
      <StickyProgressBar
        percent={percent}
        completedCount={completedCount}
        totalCount={SECTION_LABELS.length}
        cardRef={progressCardRef}
      />

      <section className="bg-white">
        <div className="container">
          {/*
            Form.cshtml went straight from `<div class="content">` to the upload
            card with no page heading of any kind, so the form began abruptly
            under the navbar. This is the same centred heading block the Vacancy
            detail page uses, so it reads as part of the same site.
          */}
          <div className="row justify-content-center text-center">
            <div className="col-md-8 col-xl-6">
              <h2 className="section-title">Personal Profile</h2>
              <p className="profile-page-subtitle mt-lg-4 mt-2 mb-4 mb-md-5">
                {user?.FullName ? (
                  <>
                    Hello, <span className="fw-semibold">{user.FullName}</span>. Complete the
                    sections below so you can apply for vacancies.
                  </>
                ) : (
                  'Complete the sections below so you can apply for vacancies.'
                )}
              </p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-8">
              {/* Photo and CV upload card */}
              <div className="card p-4 mb-4">
                <div className="text-center mb-4">
                  <div className="profile-avatar-container mb-3" id="profilePhotoContainer">
                    {/*
                      The saved photo is rendered from the base64 the API
                      returns, never from PhotoPath — that is a UNC share and
                      the browser blocks it outright.
                    */}
                    {photoPreview || savedPhotoUrl ? (
                      <img
                        src={photoPreview || savedPhotoUrl}
                        alt="Profile Photo"
                        className="profile-photo"
                      />
                    ) : (
                      <div className="profile-avatar" id="profileAvatarIcon">
                        <i className="fas fa-user" style={{ fontSize: '6rem', color: '#6c757d' }} />
                      </div>
                    )}
                  </div>

                  <div className="profile-name">{user?.FullName}</div>
                  <a href={`mailto:${user?.Email ?? ''}`} className="profile-email">
                    {user?.Email}
                  </a>

                  <div className="mt-4">
                    <UploadField
                      id="filePhoto"
                      label="Upload photo"
                      required
                      accepted="(File accepted: jpg, jpeg, png)"
                      buttonIcon="fas fa-camera"
                      accept=".jpg,.jpeg,.png"
                      maxBytes={2 * 1024 * 1024}
                      file={photo}
                      onFileChange={setPhoto}
                      existingName={existingPhoto ? fileNameOf(existingPhoto) : null}
                      successText="Photo uploaded successfully!"
                      invalid={showErrors && !photo && !existingPhoto}
                    />

                    <UploadField
                      id="fileCV"
                      label="Upload CV / Resume"
                      required
                      hint="Please upload your CV (PDF only) to unlock the rest of the application form."
                      buttonIcon="fas fa-file-alt"
                      accept=".pdf"
                      maxBytes={5 * 1024 * 1024}
                      file={cv}
                      onFileChange={(file) => void handleCvSelected(file)}
                      existingName={existingCv ? fileNameOf(existingCv) : null}
                      successText="CV uploaded successfully!"
                      invalid={showErrors && !cv && !existingCv}
                    />

                    {isExtracting && (
                      <div className="text-start text-muted small mt-2">
                        <span
                          className="spinner-border spinner-border-sm me-2 text-primary"
                          role="status"
                        />
                        Reading your CV to fill the form...
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <CollapsibleSection id="personalInformation" icon="fas fa-user" title="Personal Information" defaultOpen>
                <PersonalInformationSection
                  value={personal}
                  onChange={(patch) => setPersonal((p) => ({ ...p, ...patch }))}
                  showErrors={showErrors}
                />
              </CollapsibleSection>

              <ListSection
                id="education"
                icon="fas fa-graduation-cap"
                title="Education"
                onAdd={() => setOpenModal('education')}
              >
                <EducationSection
                  rows={education}
                  onChange={setEducation}
                  showErrors={showErrors}
                  isModalOpen={openModal === 'education'}
                  onCloseModal={() => setOpenModal(null)}
                />
              </ListSection>

              <ListSection
                id="workExperience"
                icon="fas fa-suitcase"
                title="Work Experience"
                onAdd={() => setOpenModal('work')}
              >
                <WorkExperienceSection
                  rows={work}
                  onChange={setWork}
                  isModalOpen={openModal === 'work'}
                  onCloseModal={() => setOpenModal(null)}
                />
              </ListSection>

              <ListSection
                id="organizationExperience"
                icon="fas fa-address-card"
                title="Organization"
                onAdd={() => setOpenModal('organization')}
              >
                <OrganizationSection
                  rows={organizations}
                  onChange={setOrganizations}
                  isModalOpen={openModal === 'organization'}
                  onCloseModal={() => setOpenModal(null)}
                />
              </ListSection>

              <CollapsibleSection
                id="Achievement"
                icon="fas fa-certificate"
                title="Achievement, Skill & Certification"
                defaultOpen
              >
                <AchievementSection
                  value={achievement}
                  onChange={(patch) => setAchievement((a) => ({ ...a, ...patch }))}
                  certificate={certificate}
                  onCertificateChange={setCertificate}
                  existingCertificate={
                    savedAchievement.data?.Data?.CertificationPath
                      ? fileNameOf(savedAchievement.data.Data.CertificationPath)
                      : null
                  }
                  showErrors={showErrors}
                />
              </CollapsibleSection>

              <CollapsibleSection
                id="Family"
                icon="fas fa-users"
                title="Family and Relative Information"
                defaultOpen
              >
                <FamilySection
                  value={family}
                  onChange={(patch) => setFamily((f) => ({ ...f, ...patch }))}
                />
              </CollapsibleSection>

              <CollapsibleSection
                id="medicalHistory"
                icon="fas fa-notes-medical"
                title="Medical History"
                defaultOpen
              >
                <MedicalHistorySection
                  value={medical}
                  onChange={(patch) => setMedical((m) => ({ ...m, ...patch }))}
                />
              </CollapsibleSection>
            </div>

            {/* Progress Tracking Card */}
            <div className="col-md-4 gap-2">
              <div className="progress-card-container">
                <div className="progress-card" ref={progressCardRef}>
                  <div className="progress-header">
                    <h3 className="progress-title">Profile Completeness</h3>
                    <span className="progress-counter">
                      {completedCount} of {SECTION_LABELS.length} completed
                    </span>
                  </div>

                  <div className="progress-main">
                    <div className="progress-main-bar">
                      <div
                        className="progress-main-fill"
                        id="mainProgressBar"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    <div className="progress-label">{percent}% Complete</div>
                  </div>

                  <ul className="progress-sections">
                    {SECTION_LABELS.map((label, index) => {
                      const isDone = completed[index] ?? false
                      const state = isDone ? 'completed' : 'pending'
                      return (
                        <li className="progress-section" key={label}>
                          <div className={`progress-section-icon ${state}`}>
                            <i className={isDone ? 'fas fa-check' : SECTION_ICONS[index]} />
                          </div>
                          <span className={`progress-section-text ${state}`}>{label}</span>
                        </li>
                      )
                    })}
                  </ul>
                </div>

                <button
                  type="button"
                  className="btn btn-primary w-100"
                  id="btnSaveProfile"
                  disabled={isSaving}
                  onClick={() => void handleSubmit()}
                >
                  {isSaving ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Saving...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-upload" /> Submit
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

/**
 * Personal Information / Achievement / Family / Medical History: a header that
 * toggles the body.
 */
function CollapsibleSection({
  id,
  icon,
  title,
  children,
  defaultOpen = false,
}: {
  id: string
  icon: string
  title: string
  children: ReactNode
  defaultOpen?: boolean
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  return (
    <div className="card border-0 mb-4 accordion-section">
      <div className="card-body p-0">
        <div className="accordion">
          <div className="accordion-item border-0">
            <div className="profile-section-header p-3">
              <h2 className="accordion-header mb-0" id={id}>
                <div className="d-flex justify-content-between align-items-center w-100">
                  <button
                    className="profile-accordion-button d-flex justify-content-between align-items-center w-100"
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setIsOpen((open) => !open)}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div className="profile-section-icon bg-primary text-white d-flex align-items-center justify-content-center">
                        <i className={icon} />
                      </div>
                      <h5 className="profile-section-title mb-0">{title}</h5>
                    </div>
                  </button>
                </div>
              </h2>
            </div>
            {isOpen && <div className="accordion-body profile-accordion-body">{children}</div>}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Education / Work Experience / Organization: never collapsed, with an "Add"
 * button in the header that opens the section's modal.
 */
function ListSection({
  id,
  icon,
  title,
  onAdd,
  children,
}: {
  id: string
  icon: string
  title: string
  onAdd: () => void
  children: ReactNode
}): JSX.Element {
  return (
    <div className="card border-0 mb-4 accordion-section">
      <div className="accordion-item border-0">
        <div className="profile-section-header p-3">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div className="profile-accordion-button d-flex align-items-center gap-3 flex-grow-1">
              <div className="profile-section-icon bg-primary text-white d-flex align-items-center justify-content-center">
                <i className={icon} />
              </div>
              <h5 className="profile-section-title mb-0">{title}</h5>
            </div>
            <button
              type="button"
              className="btn btn-primary profile-section-btn d-flex align-items-center gap-2"
              id={`addNew${id}`}
              onClick={onAdd}
            >
              <i className="fas fa-plus" />
              Add
            </button>
          </div>
        </div>
        <div className="row px-3" id={`${id}List`}>
          {children}
        </div>
      </div>
    </div>
  )
}

/** `ProfileForm.extractFileName` — the stored value is a path. */
function fileNameOf(path: string): string {
  return path.split(/[\\/]/).pop() ?? path
}

/**
 * Re-selects the Job Preferences option from what was stored.
 *
 * The saved string is the option's label ("Information Technology"), not its
 * value ("IT"), so matching is done on the label first. The value is accepted
 * too: rows written by other flows may hold either, and an unrecognised string
 * simply leaves the field untouched rather than blanking a valid selection.
 */
function jobPreferenceFrom(
  stored: string | null | undefined,
): Partial<Pick<PersonalInfoState, 'JobPreferences' | 'JobPreferencesLabel'>> {
  const text = stored?.trim()
  if (!text) return {}
  const match =
    JOB_PREFERENCES.find((option) => option.label.toLowerCase() === text.toLowerCase()) ??
    JOB_PREFERENCES.find((option) => option.value.toLowerCase() === text.toLowerCase())
  if (!match) return {}
  return { JobPreferences: match.value, JobPreferencesLabel: match.label }
}
